import { useState } from 'react'
import { Button, Stack, CircularProgress, Alert } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { exportAsImage } from '~/util/export/exportImage'
import { exportAsPdf, exportElementsAsPdf } from '~/util/export/exportPdf'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

interface PageExportButtonsProps {
  target: () => HTMLElement | null
  filenameBase: string
  disabled?: boolean
}

export function PageExportButtons({
  target,
  filenameBase,
  disabled = false,
}: PageExportButtonsProps) {
  const [pending, setPending] = useState<'png' | 'pdf' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async (format: 'png' | 'pdf') => {
    setError(null)
    const el = target()
    if (!el) return // defensive no-op (parent should guard via disabled prop)
    setPending(format)
    try {
      if (format === 'png') {
        await exportAsImage(el, `${filenameBase}.png`)
      } else {
        // Page-break-aware per-pin placement: each pinned chart is rasterized and placed
        // whole, never sliced across a page seam (Task 12). Pages that aren't a pin
        // dashboard (no [data-export-pin] markers) fall back to the whole-container path.
        const pinEls = Array.from(el.querySelectorAll<HTMLElement>('[data-export-pin]'))
        if (pinEls.length > 0) {
          await exportElementsAsPdf(pinEls, `${filenameBase}.pdf`)
        } else {
          await exportAsPdf(el, `${filenameBase}.pdf`)
        }
      }
    } catch (err) {
      console.error(`[PageExportButtons] ${format} export failed`, err)
      setError(DIALOGS.exportFailed)
    } finally {
      setPending(null)
    }
  }

  const isDisabled = disabled || pending !== null

  return (
    <Stack spacing={0.5} alignItems="flex-end">
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          startIcon={pending === 'png' ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={() => run('png')}
          disabled={isDisabled}
        >
          {DIALOGS.exportImage}
        </Button>
        <Button
          size="small"
          startIcon={pending === 'pdf' ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={() => run('pdf')}
          disabled={isDisabled}
        >
          {DIALOGS.exportPdf}
        </Button>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  )
}
